import React, { useState } from "react";
import { Save, X, UserCog } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const input = "w-full rsp-card px-3 py-2 bg-[var(--rsp-card)] outline-none focus:border-[var(--rsp-gold)] text-sm";
const label = "eyebrow block mb-1";

const BCT_FORTS = ["Fort Jackson, SC", "Fort Moore, GA", "Fort Leonard Wood, MO", "Fort Sill, OK"];

// Full recruit-profile editor. `recruiters` (array) is passed only for admins to allow reassignment.
export default function EditRecruitModal({ recruit, recruiters, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: recruit.name || "",
    email: recruit.email || "",
    phone: recruit.phone || "",
    contact_email: recruit.contact_email || "",
    rank: recruit.rank || "",
    mos: recruit.mos || "",
    ship_date: recruit.ship_date || "",
    ait_end_date: recruit.ait_end_date || "",
    bct_location: recruit.bct_location || "",
    gender: (recruit.gender || "").toLowerCase(),
    recruiter: recruit.recruiter || "",
    has_cac: !!recruit.has_cac,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required.");
    if (!form.email.trim()) return toast.error("Email is required.");
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, phone: form.phone, contact_email: form.contact_email,
        rank: form.rank, mos: form.mos, ship_date: form.ship_date, ait_end_date: form.ait_end_date,
        bct_location: form.bct_location, gender: form.gender, has_cac: form.has_cac,
      };
      if (recruiters) payload.recruiter = form.recruiter || "";
      await api.put(`/me/recruits/${recruit.user_id}/profile`, payload);
      toast.success("Recruit profile updated.");
      onSaved && onSaved();
      onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not update recruit.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="rsp-card p-5 w-full max-w-lg space-y-3 max-h-[90vh] overflow-y-auto rsp-scroll"
        onClick={(e) => e.stopPropagation()} data-testid="edit-recruit-modal">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-head uppercase tracking-wide text-lg">
            <UserCog size={20} className="gold" /> Edit Recruit
          </div>
          <button onClick={onClose} className="text-[var(--rsp-muted)] hover:text-[var(--rsp-cream)]" data-testid="edit-recruit-cancel-x"><X size={18} /></button>
        </div>

        <div>
          <span className={label}>Full Name</span>
          <input className={input} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Name" data-testid="edit-recruit-name" />
        </div>
        <div>
          <span className={label}>Login Email</span>
          <input className={input} value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Email" data-testid="edit-recruit-email" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className={label}>Phone</span>
            <input className={input} value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Phone" data-testid="edit-recruit-phone" />
          </div>
          <div>
            <span className={label}>Contact Email</span>
            <input className={input} value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="Contact email" data-testid="edit-recruit-contact-email" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className={label}>Rank</span>
            <input className={input} value={form.rank} onChange={(e) => set("rank", e.target.value)} placeholder="Rank" data-testid="edit-recruit-rank" />
          </div>
          <div>
            <span className={label}>MOS</span>
            <input className={input} value={form.mos} onChange={(e) => set("mos", e.target.value)} placeholder="MOS" data-testid="edit-recruit-mos" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className={label}>Ship Date</span>
            <input type="date" className={input} value={form.ship_date ? form.ship_date.slice(0, 10) : ""} onChange={(e) => set("ship_date", e.target.value)} data-testid="edit-recruit-ship-date" />
          </div>
          <div>
            <span className={label}>AIT End Date</span>
            <input type="date" className={input} value={form.ait_end_date ? form.ait_end_date.slice(0, 10) : ""} onChange={(e) => set("ait_end_date", e.target.value)} data-testid="edit-recruit-ait" />
          </div>
        </div>
        <div>
          <span className={label}>BCT Location</span>
          <select className={input} value={form.bct_location} onChange={(e) => set("bct_location", e.target.value)} data-testid="edit-recruit-bct">
            <option value="">— Select —</option>
            {BCT_FORTS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </div>
        {recruiters && (
          <div>
            <span className={label}>Assigned Recruiter</span>
            <select className={input} value={form.recruiter} onChange={(e) => set("recruiter", e.target.value)} data-testid="edit-recruit-recruiter">
              <option value="">Unassigned</option>
              {recruiters.map((r) => <option key={r.id || r.name} value={r.name}>{r.name}</option>)}
            </select>
          </div>
        )}
        <div>
          <span className={label}>Gender</span>
          <div className="flex gap-2">
            {["male", "female"].map((g) => (
              <button key={g} type="button" onClick={() => set("gender", form.gender === g ? "" : g)}
                data-testid={`edit-recruit-gender-${g}`}
                className={`flex-1 py-2 rounded-lg border text-sm capitalize transition-colors ${form.gender === g ? "border-[var(--rsp-gold)] text-[var(--rsp-gold)] bg-[var(--rsp-gold)]/10" : "border-[var(--rsp-border)] text-[var(--rsp-gray)]"}`}>
                {form.gender === g ? "✓ " : ""}{g}
              </button>
            ))}
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.has_cac} onChange={(e) => set("has_cac", e.target.checked)} className="accent-[var(--rsp-gold)]" data-testid="edit-recruit-cac" />
          CAC issued
        </label>

        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2 rounded-lg border border-[var(--rsp-border)] text-[var(--rsp-gray)]" data-testid="edit-recruit-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-gold flex-1 py-2 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="edit-recruit-save">
            <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
